import moment from 'moment';
import Validator from 'validatorjs';

import { DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import $savedFilters from 'app/stores/savedFilter/$savedFilters';
import { PERCENT_METRICS } from 'app/util/constants';
import { RATIO_CONDITION_DIRECTIONS } from '@kentik/ui-shared/alerting/constants';

function transformFilterGroups(value = []) {
  return value.map((filterGroup) => ({ filterGroups: [], not: false, connector: 'All', ...filterGroup }));
}

Validator.register(
  'isValidSavedFilterId',
  /* eslint-disable prefer-arrow-callback */
  function isValidSavedFilterId(value) {
    if (!value) {
      return false;
    }

    return !!$savedFilters.getSavedFilterById(value);
  },
  'Invalid saved filter'
);

function getFilterGroups(prefix, blacklist, options, depth = 0) {
  const canNestFilterGroups = depth < 5;

  const filterGroups = {
    [`${prefix}.filterGroups`]: {
      isComplexArray: true,
      transform: {
        in: (value) => (canNestFilterGroups ? transformFilterGroups(value) : value)
      },
      rules: options.filterGroupsRules,
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].name`]: {
      label: 'Name',
      rules: [{ requiredIfMultiple: ['filterDimensionsEnabled', true, 'named', true] }],
      messages: {
        required_if: 'Series name is required'
      },
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].named`]: {
      defaultValue: false,
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].connector`]: {
      defaultValue: 'All',
      options: [
        {
          label: 'All',
          value: 'All'
        },
        {
          label: 'Any',
          value: 'Any'
        }
      ],
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].not`]: {
      defaultValue: false,
      options: [
        {
          label: 'Exclude',
          value: true
        },
        {
          label: 'Include',
          value: false
        }
      ],
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].autoAdded`]: {
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].disabled`]: {
      suppressAutoSubmit: true,
      defaultValue: false
    },

    [`${prefix}.filterGroups[].filters`]: {
      isComplexArray: true,
      label: 'Filter Group',
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].saved_filters`]: {
      isComplexArray: true,
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].saved_filters[].filter_id`]: {
      rules: 'required|isValidSavedFilterId',
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].saved_filters[].is_not`]: {
      defaultValue: false,
      options: [
        {
          label: 'Exclude Results',
          value: true,
          icon: 'cross'
        },
        {
          label: 'Include Results',
          value: false,
          icon: 'tick'
        }
      ],
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].filters[].disabled`]: {
      suppressAutoSubmit: true,
      defaultValue: false
    },

    [`${prefix}.filterGroups[].filters[].filterField`]: {
      defaultValue: options.isMetricFilter ? '' : 'dst_as',
      // Metric filters use the 'metric' field as the primary key, so filterField is optional
      rules: options.isMetricFilter ? undefined : 'required|noConflictingUniques',
      optionsBlacklist: new Set(blacklist),
      label: 'field',
      suppressAutoSubmit: true
    },

    // recon + metric filters (UDE)
    [`${prefix}.filterGroups[].filters[].metric`]: {
      defaultValue: '',
      label: 'metric',
      suppressAutoSubmit: true
    },

    // recon + metric filters (UDE)
    [`${prefix}.filterGroups[].filters[].aggregate`]: {
      defaultValue: '',
      label: 'aggregate',
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].filters[].operator`]: {
      defaultValue: '=',
      rules: 'required',
      label: 'operator',
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].filters[].filterValue`]: {
      placeholder: 'Value...',
      label: 'value',
      messages: {
        required: 'Please enter a value'
      },
      suppressAutoSubmit: true
    },

    [`${prefix}.filterGroups[].filters[].rightFilterField`]: {
      label: 'value',
      messages: {
        required: 'A right filter field is required'
      },
      suppressAutoSubmit: true
    }
  };

  if (canNestFilterGroups) {
    Object.assign(filterGroups, getFilterGroups(`${prefix}.filterGroups[]`, blacklist, {}, depth + 1));
  }

  return filterGroups;
}

export function generateFilterFields(fieldName = 'filters', blacklist = [], options = {}) {
  const filterFields = {
    [`${fieldName}.connector`]: {
      defaultValue: 'All',
      options: [
        {
          label: 'All',
          value: 'All'
        },
        {
          label: 'Any',
          value: 'Any'
        }
      ],
      suppressAutoSubmit: true
    }
  };

  Object.assign(filterFields, getFilterGroups(fieldName, blacklist, options));

  return filterFields;
}

export function generateTimeFields(rootFieldName) {
  const prefix = rootFieldName ? `${rootFieldName}.` : '';

  const timeInTransform = (value, form) => {
    const time_format = form.getValue(`${prefix}time_format`);
    const lookback_seconds = form.getValue(`${prefix}lookback_seconds`);
    if (!value || lookback_seconds) {
      return value;
    }

    if (!time_format || time_format === 'UTC') {
      // we're assuming UTC here but we want to force the format so timezone offsets don't throw moment off inside the field
      return moment.utc(value).format(DEFAULT_DATETIME_FORMAT);
    }
    // converting UTC time to local time.
    return moment(moment.utc(value).valueOf()).format(DEFAULT_DATETIME_FORMAT);
  };

  const timeOutTransform = (value, form) => {
    const time_format = form.getValue(`${prefix}time_format`);
    const lookback_seconds = form.getValue(`${prefix}lookback_seconds`);
    if (!value || lookback_seconds) {
      return value;
    }

    // sometimes we get a Date object here because the user used the form field to change something
    // Blueprint Date components return Date objects.
    if (value instanceof Date) {
      value = moment(value).format(DEFAULT_DATETIME_FORMAT);
    }
    if (!time_format || time_format === 'UTC') {
      // UTC mode, so just return the value that came out of the field
      return value;
    }
    // Local mode, so take value and convert it to UTC.
    return moment(value).utc().format(DEFAULT_DATETIME_FORMAT);
  };

  return {
    [`${prefix}lookback_seconds`]: {
      label: 'Show The',
      defaultValue: 3600
    },
    [`${prefix}from_to_lookback`]: {
      label: 'Show The',
      defaultValue: 0
    },
    [`${prefix}time_format`]: {
      label: 'Time Zone',
      options: [
        { value: 'UTC', label: 'UTC' },
        { value: 'Local', label: 'Local' }
      ]
    },
    [`${prefix}starting_time`]: {
      label: 'From',
      messages: {
        before: 'Start date must be before end date'
      },
      transform: {
        in: timeInTransform,
        out: timeOutTransform
      }
    },
    [`${prefix}ending_time`]: {
      label: 'To',
      transform: {
        in: timeInTransform,
        out: timeOutTransform
      }
    },
    [`${prefix}period_over_period`]: {
      label: 'Compare over previous period'
    },
    [`${prefix}period_over_period_lookback`]: {
      label: 'previous offset',
      rules: 'integer'
    },
    [`${prefix}period_over_period_lookback_unit`]: {
      options: [
        { label: 'hour', value: 'hour' },
        { label: 'day', value: 'day' },
        { label: 'week', value: 'week' },
        { label: 'month', value: 'month' }
      ]
    },
    [`${prefix}use_alt_timestamp_field`]: {
      label: 'Use Cloud Timestamps (Beta)'
    }
  };
}

export const prefixValidationName = (validatorName, maybePrefix) =>
  maybePrefix ? `${maybePrefix}_${validatorName}` : validatorName;

export const getPrefix = (maybePrefix) => {
  if (maybePrefix) {
    return maybePrefix.endsWith('.') ? maybePrefix : `${maybePrefix}.`;
  }

  return '';
};

export const numericTransform = (value) => {
  const numericValue = parseInt(value, 10);

  if (Number.isNaN(numericValue)) {
    return '';
  }

  return numericValue;
};

export const transformMetricValueIn = (metric, value, transformPercentMetrics = true) => {
  if (value === null) {
    return '';
  }

  if (transformPercentMetrics && PERCENT_METRICS.includes(metric)) {
    return value / 1000;
  }

  return value;
};

export const transformMetricValueOut = (metric, value, transformPercentMetrics = true) => {
  if (!value && value !== 0) {
    return value;
  }

  if (transformPercentMetrics && PERCENT_METRICS.includes(metric)) {
    return Math.round(value * 1000);
  }

  if (metric === 'fps') {
    return Math.max(value * 1, 1);
  }

  return Number(value);
};

export const numericFloatTransform = (value) => {
  const numericValue = parseFloat(value, 10);

  if (Number.isNaN(numericValue)) {
    return '';
  }

  return numericValue;
};

export const transformRatioMetricIn = (value) => parseInt(value);

export const transformDirectionIn = (value) => {
  if (value === true || value === false) {
    return value;
  }

  return value === RATIO_CONDITION_DIRECTIONS.BOTH;
};

export const transformDirectionOut = (value) => {
  if (value === true) {
    return RATIO_CONDITION_DIRECTIONS.BOTH;
  }

  if (value === false) {
    return RATIO_CONDITION_DIRECTIONS.LEFT;
  }

  return value;
};

export const stringTransform = (value) => `${value}`;
